import { useState, useMemo, MouseEvent, ReactNode } from 'react';
import { FCX } from '@datassential/platform-ui-lib';
import cn from 'classnames';
import Icon from '@components/Icon/Icon';
import { IconArrowBottom } from '@icons';
import { OptionsType } from './byoTable.helpers';

interface ColumnHeaderProps {
    content: ReactNode;
    options?: OptionsType;
    onOptionChange?: (optionId: string) => void;
}

const ColumnHeader: FCX<ColumnHeaderProps> = ({
    content,
    options,
    onOptionChange,
}) => {
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [selectedOptionId, setSelectedOptionId] = useState<string>(() => {
        if (options && options.length > 0) {
            const activeOption = options.find(opt => opt.isActive);
            return activeOption ? activeOption.id : options.at(-1)!.id;
        }
        return '';
    });

    const selectedOption = useMemo(() => {
        if (!options || options.length === 0) return null;
        return options.find(opt => opt.id === selectedOptionId);
    }, [options, selectedOptionId]);

    const handleOptionSelect = (optionId: string) => {
        setSelectedOptionId(optionId);
        setIsDropdownOpen(false);
        if (onOptionChange) {
            onOptionChange(optionId);
        }
    };

    const toggleDropdown = (e: MouseEvent) => {
        e.stopPropagation();
        setIsDropdownOpen(!isDropdownOpen);
    };

    const displayContent = useMemo(() => {
        if (!options || options.length === 0) {
            return content;
        }

        if (options.length === 1) {
            return (
                <div>
                    {content}
                    <div className="ByoTable__header-dropdown">
                        {selectedOption?.title}
                    </div>
                </div>
            );
        }

        return (
            <div>
                {content}
                <div className="ByoTable__header-dropdown" onClick={toggleDropdown}>
                    {selectedOption?.title}
                    <div
                        className={cn('ByoTable__header-dropdown-arrow', { 'ByoTable__header-dropdown-arrow--open': isDropdownOpen })}>
                        <Icon type='small'>
                            <IconArrowBottom/>
                        </Icon>
                    </div>
                </div>
                {isDropdownOpen && (
                    <div className="ByoTable__header-dropdown-menu"
                         style={{ position: 'absolute', zIndex: 10, left: 0, top: '100%' }}>
                        {options.filter(option => option.id !== selectedOptionId).map(option => (
                            <div
                                key={option.id}
                                className="ByoTable__header-dropdown-item"
                                onClick={(event) => {
                                    event.stopPropagation();
                                    handleOptionSelect(option.id);
                                }}
                            >
                                {option.title}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
    }, [
        content,
        options,
        selectedOptionId,
        selectedOption,
        isDropdownOpen,
        handleOptionSelect,
    ]);

    return (
        <span>
            {displayContent}
        </span>
    );
};

export default ColumnHeader;
